import { supabase } from "@/lovable-demo/runtime/client";
import type { AuditEventInput } from "./types";

/**
 * Shared audit / decision-lineage service.
 *
 * The visible Problem Record history (`record.audit[]`) is written exactly as
 * before. This appends the same event to the queryable, append-only
 * `audit_events` table so the trail survives record saves and can be reported
 * across records.
 *
 * CONTROLLED GOVERNANCE EVENT (Class A). `recordAuditEvent` is confirmed: it
 * throws when the write fails so the caller can refuse to represent the
 * governed action as recorded. Only informational telemetry may use the
 * non-blocking `logAuditEvent` wrapper.
 */

export class GovernanceWriteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GovernanceWriteError";
  }
}

async function currentActor(): Promise<{ id: string | null; label: string }> {
  try {
    const { data } = await supabase.auth.getUser();
    const user = data.user;
    if (!user) return { id: null, label: "" };
    const meta = (user.user_metadata ?? {}) as { full_name?: string };
    return { id: user.id, label: meta.full_name || user.email || "" };
  } catch {
    return { id: null, label: "" };
  }
}

/** Confirmed audit write. Throws on failure — never swallow this. */
export async function recordAuditEvent(input: AuditEventInput): Promise<string> {
  const actor = await currentActor();
  const { data, error } = await supabase
    .from("audit_events")
    .insert({
      entity_type: input.entityType ?? "problem_record",
      entity_id: input.entityId,
      record_code: input.recordCode ?? "",
      stage_id: input.stageId ?? "",
      event_type: input.eventType,
      action: input.action,
      clause: input.clause ?? "",
      detail: input.detail ?? "",
      actor_user_id: actor.id,
      actor_label: actor.label || input.actorLabel || "",
      decision_verdict: input.decisionVerdict ?? null,
      recommendation_id: input.recommendationId ?? null,
      authority_rule_key: input.authorityRuleKey ?? null,
      payload: (input.payload ?? {}) as never,
    })
    .select("id")
    .single();
  if (error || !data?.id) {
    throw new GovernanceWriteError(error?.message ?? "The audit entry could not be written.");
  }
  return data.id;
}

/**
 * TELEMETRY ONLY (Class B). Non-blocking; failures are ignored.
 * Never use this for a decision, approval, stage closure or evidence gate.
 */
export function logAuditEvent(input: AuditEventInput): void {
  void recordAuditEvent(input).catch(() => undefined);
}

export async function listAuditEvents(entityId: string) {
  const { data } = await supabase
    .from("audit_events")
    .select("*")
    .eq("entity_id", entityId)
    .order("occurred_at", { ascending: false });
  return data ?? [];
}

export async function getSignedInActor() {
  return currentActor();
}
